import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import Reveal from "../components/motion/Reveal";
import { api } from "../lib/api";
import { openInteractiveDemo } from "../components/demo/DemoLauncher";
import {
  Mail, Send, CheckCircle2, Clock, MessagesSquare, PlayCircle, ArrowRight, Loader2,
} from "lucide-react";

const SUBJECTS = [
  { value: "demo", label: "Demander une démonstration" },
  { value: "commercial", label: "Question commerciale" },
  { value: "support", label: "Support" },
  { value: "partenariat", label: "Partenariat" },
  { value: "presse", label: "Presse" },
  { value: "autre", label: "Autre" },
];

const FLEET_SIZES = ["1 à 5 véhicules", "6 à 20 véhicules", "21 à 50 véhicules", "Plus de 50 véhicules"];

const INPUT_CLS =
  "w-full h-11 rounded-xl border bg-white px-3.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-violet-500 focus:ring-2 focus:ring-violet-100";

function Field({ id, label, required, error, children }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[13px] font-semibold text-slate-700 mb-1.5">
        {label} {required && <span className="text-violet-600">*</span>}
      </label>
      {children}
      {error && <p className="mt-1.5 text-xs text-red-600" data-testid={`contact-error-${id}`}>{error}</p>}
    </div>
  );
}

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", company: "", fleet_size: "", subject: "", message: "", consent: false };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    document.title = "Contact — Parlons de votre projet | Junago";
    window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.first_name.trim()) er.first_name = "Votre prénom est requis.";
    if (!form.last_name.trim()) er.last_name = "Votre nom est requis.";
    if (!form.email.trim()) er.email = "Votre email est requis.";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) er.email = "Cette adresse email n'est pas valide.";
    if (!form.subject) er.subject = "Choisissez un sujet.";
    if (!form.message.trim()) er.message = "Décrivez votre demande en quelques mots.";
    else if (form.message.trim().length < 10) er.message = "Votre message est un peu court (10 caractères minimum).";
    if (!form.consent) er.consent = "Votre consentement est nécessaire pour traiter votre demande.";
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length > 0) return;
    setSubmitting(true);
    setServerError("");
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      setServerError(err.response?.data?.detail || "Une erreur est survenue. Merci de réessayer dans un instant.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      {/* ─── Hero épuré ─── */}
      <section className="relative pt-12 md:pt-16 pb-10 overflow-hidden" data-testid="contact-hero">
        <div aria-hidden className="absolute top-0 left-1/2 -translate-x-1/2 h-[360px] w-[680px] rounded-full bg-violet-400/10 blur-[110px] pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-50 border border-violet-200">
              <Mail className="h-3.5 w-3.5 text-violet-600" />
              <span className="text-xs font-bold tracking-[0.15em] uppercase text-violet-700">Contact</span>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.08] text-slate-900" data-testid="contact-title">
              Parlons de votre projet
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Que vous souhaitiez découvrir Junago, organiser une démonstration, poser une question
              ou envisager un partenariat, notre équipe vous répond.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── Formulaire + accompagnement ─── */}
      <section className="max-w-6xl mx-auto px-6 pb-20 md:pb-28">
        <div className="grid lg:grid-cols-[1fr,340px] gap-10 items-start">
          {/* Formulaire */}
          <Reveal y={30}>
            {sent ? (
              <div className="rounded-3xl border border-emerald-200 bg-emerald-50/60 p-10 md:p-14 text-center" data-testid="contact-success">
                <span className="inline-flex h-14 w-14 rounded-full bg-emerald-500 items-center justify-center">
                  <CheckCircle2 className="h-7 w-7 text-white" />
                </span>
                <h2 className="mt-5 font-heading text-2xl font-medium tracking-tight text-slate-900">Votre demande a bien été enregistrée.</h2>
                <p className="mt-3 text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
                  Merci ! Notre équipe prend connaissance de chaque demande et vous répondra
                  à l'adresse indiquée, généralement sous 1 à 2 jours ouvrés.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
                  <button type="button" onClick={openInteractiveDemo} className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold" data-testid="contact-success-demo">
                    <PlayCircle className="h-4 w-4" /> Essayer la démo en attendant
                  </button>
                  <Link to="/" className="btn-jn-secondary inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold">
                    Retour à l'accueil
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="rounded-3xl border border-slate-200 bg-white p-7 md:p-10 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.25)]" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field id="first_name" label="Prénom" required error={errors.first_name}>
                    <input id="first_name" type="text" value={form.first_name} onChange={set("first_name")} placeholder="Camille"
                      aria-invalid={!!errors.first_name} className={`${INPUT_CLS} ${errors.first_name ? "border-red-300" : "border-slate-200"}`} data-testid="contact-first-name" />
                  </Field>
                  <Field id="last_name" label="Nom" required error={errors.last_name}>
                    <input id="last_name" type="text" value={form.last_name} onChange={set("last_name")} placeholder="Durand"
                      aria-invalid={!!errors.last_name} className={`${INPUT_CLS} ${errors.last_name ? "border-red-300" : "border-slate-200"}`} data-testid="contact-last-name" />
                  </Field>
                  <Field id="email" label="Email professionnel" required error={errors.email}>
                    <input id="email" type="email" value={form.email} onChange={set("email")} placeholder="camille@votre-agence.fr"
                      aria-invalid={!!errors.email} className={`${INPUT_CLS} ${errors.email ? "border-red-300" : "border-slate-200"}`} data-testid="contact-email" />
                  </Field>
                  <Field id="phone" label="Téléphone" error={errors.phone}>
                    <input id="phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="06 12 34 56 78"
                      className={`${INPUT_CLS} border-slate-200`} data-testid="contact-phone" />
                  </Field>
                  <Field id="company" label="Nom de l'entreprise" error={errors.company}>
                    <input id="company" type="text" value={form.company} onChange={set("company")} placeholder="Votre agence"
                      className={`${INPUT_CLS} border-slate-200`} data-testid="contact-company" />
                  </Field>
                  <Field id="fleet_size" label="Nombre de véhicules" error={errors.fleet_size}>
                    <select id="fleet_size" value={form.fleet_size} onChange={set("fleet_size")}
                      className={`${INPUT_CLS} border-slate-200 ${form.fleet_size ? "" : "text-slate-400"}`} data-testid="contact-fleet-size">
                      <option value="">Sélectionner…</option>
                      {FLEET_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field id="subject" label="Sujet" required error={errors.subject}>
                      <select id="subject" value={form.subject} onChange={set("subject")}
                        aria-invalid={!!errors.subject}
                        className={`${INPUT_CLS} ${errors.subject ? "border-red-300" : "border-slate-200"} ${form.subject ? "" : "text-slate-400"}`} data-testid="contact-subject">
                        <option value="">Choisissez un sujet…</option>
                        {SUBJECTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field id="message" label="Message" required error={errors.message}>
                      <textarea id="message" rows={5} value={form.message} onChange={set("message")}
                        placeholder="Parlez-nous de votre activité, de votre flotte, de vos besoins…"
                        aria-invalid={!!errors.message}
                        className={`w-full rounded-xl border bg-white px-3.5 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-violet-500 focus:ring-2 focus:ring-violet-100 resize-y ${errors.message ? "border-red-300" : "border-slate-200"}`}
                        data-testid="contact-message" />
                    </Field>
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="consent" className="flex items-start gap-3 cursor-pointer">
                    <input id="consent" type="checkbox" checked={form.consent} onChange={set("consent")}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" data-testid="contact-consent" />
                    <span className="text-xs text-slate-600 leading-relaxed">
                      J'accepte que Junago traite ces informations pour répondre à ma demande,
                      conformément à sa <Link to="/legal/privacy" className="text-violet-600 underline">politique de confidentialité</Link>. <span className="text-violet-600">*</span>
                    </span>
                  </label>
                  {errors.consent && <p className="mt-1.5 text-xs text-red-600" data-testid="contact-error-consent">{errors.consent}</p>}
                </div>

                {serverError && (
                  <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="contact-server-error">{serverError}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:pointer-events-none"
                  data-testid="contact-submit"
                >
                  {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Envoi en cours…</>) : (<><Send className="h-4 w-4" /> Envoyer ma demande</>)}
                </button>
              </form>
            )}
          </Reveal>

          {/* Bloc d'accompagnement */}
          <Reveal delay={150} y={30}>
            <div className="space-y-5">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                <div className="flex items-center gap-2.5">
                  <Clock className="h-4.5 w-4.5 h-5 w-5 text-violet-600" />
                  <div className="font-heading text-base font-medium text-slate-900">Ce que vous pouvez attendre</div>
                </div>
                <ul className="mt-4 space-y-2.5 text-[13px] text-slate-600">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> Une réponse personnalisée, en français, généralement sous 1 à 2 jours ouvrés.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> Un interlocuteur qui connaît le métier de la location.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> Aucun démarchage : nous répondons à votre demande, rien d'autre.</li>
                </ul>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex items-center gap-2.5">
                  <MessagesSquare className="h-5 w-5 text-violet-600" />
                  <div className="font-heading text-base font-medium text-slate-900">Nous traitons</div>
                </div>
                <p className="mt-3 text-[13px] text-slate-600 leading-relaxed">
                  Démonstrations, questions commerciales et tarifaires, support, partenariats et demandes presse.
                </p>
                <a href="mailto:support@junago.fr" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-violet-600 hover:text-violet-800 transition-colors" data-testid="contact-email-link">
                  <Mail className="h-4 w-4" /> support@junago.fr
                </a>
              </div>

              <div className="sheen-card relative overflow-hidden rounded-2xl bg-slate-950 text-white p-6">
                <div aria-hidden className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-violet-600/30 blur-2xl" />
                <div className="relative">
                  <div className="font-heading text-base font-medium">Pressé de voir Junago ?</div>
                  <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">
                    La démo interactive est ouverte, sans compte ni carte bancaire.
                  </p>
                  <button type="button" onClick={openInteractiveDemo} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-violet-300 hover:text-white transition-colors" data-testid="contact-demo-link">
                    <PlayCircle className="h-4 w-4" /> Lancer la démo <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
